<template>
    <div class="h-full">
        <div class="relative p-3 flex flex-col rounded-r-md min-w-[300px] h-full">
            <div class="flex items-center justify-between">
                <p class="font-bold">CRESiteIQ&trade;</p>
                <button
                    id="toggle-cresiteiq-button"
                    type="button"
                    @click="$store.dispatch('hideCRESiteIQWindow')"
                    class="hover:cursor-pointer"
                >
                    <inline-svg name="close" classes="w-3 h-3 fill-current"></inline-svg>
                </button>
            </div>
            <div class="flex-1 mt-2 flex flex-col gap-y-1">
                <div class="px-2">
                    <cre-site-iq-state-selector v-if="creSiteIQ.boundary == 'zip'"/>
                    <CreSiteIqPOISelector
                        v-if="localPoiEnabled"
                        :boundary="creSiteIQ.boundary"
                        :selectedPOIs="selectedPOIs"
                        :availablePOITypes="availablePOITypes"
                        @openFilterModal="openFilterModal"
                        @removePOI="removePOI"
                        @clearAllPOIs="clearAllPOIs"
                    />

                    <div class="flex flex-col" :class="{ 'mt-6': creSiteIQ.boundary == 'zip' }">
                        <div class="flex items-center gap-x-2">
                            <label class="block text-sm/6 mb-0 font-medium text-gray-900">Selected Filters</label>

                            <div v-if="creSiteIQ.selectedMetrics.length" class="flex items-center gap-x-2">
                                <button
                                    type="button"
                                    @click="openFilterModal('metrics')"
                                    class="underline text-blue-600 text-xs font-semibold"
                                >
                                    Manage
                                </button>
                                <span class="text-gray-400">|</span>
                                <button
                                    type="button"
                                    @click="clearAllMetrics"
                                    class="underline text-blue-600 text-xs font-semibold"
                                >
                                    Clear
                                </button>
                            </div>
                        </div>

                        <div class="grid">
                            <div v-for="metric in creSiteIQ.selectedMetrics" class="pb-8 grid grid-rows-2 border-b border-gray-200" :key="metric.slug">
                                <div class="flex items-center justify-between">
                                    <div class="flex items-center gap-x-2">
                                        <span class="text-sm">{{ metric.label | truncate(20) }}</span>
                                        <v-popover offset="0" trigger="click" placement="bottom-end">
                                            <button>
                                                <inline-svg classes="mt-1 w-4 h-4 stroke-blue-500 cursor-pointer" name="info"></inline-svg>
                                            </button>

                                            <template slot="popover">
                                                <div class="p-2 min-w-[250px]">
                                                    <div class="flex flex-col">
                                                        <div class="flex items-center justify-between">
                                                            <h1 class="font-semibold">{{ metric.label }}</h1>
                                                            <a v-close-popover class="cursor-pointer">
                                                                <inline-svg name="close" />
                                                            </a>
                                                        </div>

                                                        <div class="mt-2 flex items-center justify-between p-3 bg-gray-100 rounded-md">
                                                            <div class="flex items-center text-sm gap-x-1">
                                                                <span>Higher is Better</span>
                                                                <inline-svg name="arrow-up-right" classes="w-4 h-4 stroke-green-500" />
                                                            </div>
                                                            <toggle-switch-small
                                                                @input="$store.dispatch('rescore')"
                                                                v-model="metric.higher_is_better"
                                                                :value="metric.higher_is_better"
                                                                name="is_enabled">
                                                            </toggle-switch-small>
                                                        </div>
                                                        <p class="mt-5 text-sm font-medium" title="Weight %">Weight: {{ metric.weight }}%</p>
                                                    </div>
                                                </div>
                                            </template>
                                        </v-popover>
                                    </div>
                                    <select @change="changeFilterType(metric)" v-model="metric.filter_type" class="form-select text-xs max-w-fit py-1 pr-7 bg-blue-50">
                                        <option value="lt">Less Than</option>
                                        <option value="gt">Greater Than</option>
                                        <option value="range">Range</option>
                                    </select>
                                </div>
                                <div class="w-11/12 mx-auto mt-4 bg-gray-50 py-4 px-2" style="grid-template-rows: subgrid">
                                    <div
                                        class="cresiteiq-filter-slider"
                                        :data-metric="metric.slug"
                                        :data-hs-range-slider="generateSliderJson(metric)"
                                        :key="metric.slug + '-' + metric.filter_type"
                                    >
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="! creSiteIQ.selectedMetrics.length" class="px-2">
                    <div class="p-5 border-2 border-dashed border-gray-200 text-center">
                        <button
                            @click="openFilterModal('metrics')"
                            type="button"
                            class="text-blue-500 font-semibold text-sm hover:underline hover:text-blue-400"
                        >
                            Add Filter(s)
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <cre-site-iq-manage-filters ref="manageFiltersModal"
        @poi-updated="handlePOIUpdated"
        @init-sliders="initSliders" />
    </div>
</template>

<script>
    import { mapState } from 'vuex';
    import InlineSvg from "../InlineSvg";
    import HSRangeSlider from "@preline/range-slider";
    import CreSiteIqStateSelector from "./CreSiteIqStateSelector.vue";
    import CreSiteIqManageFilters from "./CreSiteIqManageFilters.vue";
    import CreSiteIqPOISelector from "./CreSiteIqPOISelector.vue";

    export default {

        components: {
            InlineSvg,
            CreSiteIqStateSelector,
            CreSiteIqManageFilters,
            CreSiteIqPOISelector,
        },
        data() {
            return {
                selectedPOIs: [],
                boundsListener: null,
                debounceTimer: null,
                lastCenter: null,
                isUpdatingPOIs: false,
                lastPoiUpdate: null, // Track last POI update to prevent duplicates
                localPoiEnabled: this.localPoiEnabled // initialize from prop if needed

            };
        },
        
        computed: {
            ...mapState(['reatlas']),
            ...mapState(['creSiteIQ']),
            
            availablePOITypes() {
                return this.creSiteIQ.poi?.availableTypes || [];
            },
            
            layers() {
                return this.reatlas.layers;
            }
        },
        watch: {
            selectedPOIs(newTypes, oldTypes) {
                if (this.canLoadPOI(this.reatlas.map.getZoom())) {
                    this.reloadPOIsForCurrentView(true);
                }
            },
            'creSiteIQ.selectedMetrics': {
                handler() {
                    this.$nextTick(() => {
                        this.initSliders();
                    });
                },
                deep: true,
                immediate: true
            }
        },
        methods: {
            clearAllMetrics() {
                this.$store.dispatch('clearAllMetrics');
            },
            shortenNumber(value) {
                value = String(value).replace(/[^0-9.]/g, '');
                value = parseFloat(value);

                if (value < 1e3) return value.toFixed(0);
                if (value >= 1e3 && value < 1e6) return +(value / 1e3).toFixed(1) + "K";
                if (value >= 1e6 && value < 1e9) return +(value / 1e6).toFixed(1) + "M";
                if (value >= 1e9 && value < 1e12) return +(value / 1e9).toFixed(1) + "B";
                if (value >= 1e12) return +(value / 1e12).toFixed(1) + "T";
            },

            formatValueForInput(value, metric) {
                let shortened = this.shortenNumber(value);
                let prefix = '';
                let postfix = '';

                if (metric.type === 'currency') {
                    prefix = '$';
                    // Ensure currency values always show 2 decimal places
                    if (value !== null && value !== undefined) {
                        const numValue = parseFloat(value);
                        if (!isNaN(numValue)) {
                            shortened = numValue.toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            });
                        }
                    }
                } else if (metric.type === 'percentage') {
                    // Ensure percentage values always show 2 decimal places
                    if (value !== null && value !== undefined) {
                        const numValue = parseFloat(value);
                        if (!isNaN(numValue)) {
                            shortened = numValue.toFixed(2);
                        }
                    }
                    postfix = '%';
                }

                return `${prefix}${shortened}${postfix}`;
            },

            parseValueFromInput(value) {
                let num = parseFloat(String(value).replace(/[^0-9.]/g, ''));
                const suffix = String(value).replace(/[^a-zA-Z]/g, '').toUpperCase();

                if (isNaN(num)) return 0;

                switch (suffix) {
                    case 'K':
                        num *= 1e3;
                        break;
                    case 'M':
                        num *= 1e6;
                        break;
                    case 'B':
                        num *= 1e9;
                        break;
                    case 'T':
                        num *= 1e12;
                        break;
                }
                return num;
            },

            canLoadPOI(zoomLevel) {
                if (zoomLevel <= this.creSiteIQ.minZoom) {
                    return false;
                }
                return true;
            },
            clearAllPOIs() {
                this.selectedPOIs.forEach(type => {
                    this.$store.dispatch('removePOIType', type);
                });

                this.selectedPOIs = [];
                this.$store.commit('setSelectedPOIs', []);  
                // Also clear modal's internal POI selection (if modal has been opened once)
                if (this.$refs.manageFiltersModal?.selectedPOIs) {
                    this.$refs.manageFiltersModal.selectedPOIs = [];
                }   
            },
            openFilterModal(type) {
                this.$store.commit('setSelectedPOIs', this.selectedPOIs);
                this.$refs.manageFiltersModal.openModal(type);
            },
            getSelectedPOIDetails() {
                return this.availablePOITypes.filter(poi => this.selectedPOIs.includes(poi.value));
            },
            
            removePOI(poiType) {
                // Remove from selected POIs
                this.selectedPOIs = this.selectedPOIs.filter(type => type !== poiType);
                
                // Update store
                this.$store.commit('setSelectedPOIs', this.selectedPOIs);
                
                // Call removePOIType action to clean up markers
                this.$store.dispatch('removePOIType', poiType);

                // Update modal if it's open and has internal state
                if (this.$refs.manageFiltersModal && this.$refs.manageFiltersModal.selectedPOIs) {
                    this.$refs.manageFiltersModal.selectedPOIs =
                        this.$refs.manageFiltersModal.selectedPOIs.filter(type => type !== poiType);
                }
                
            },
            
            handlePOIUpdated(updatedPOIs) {
                // Update local selectedPOIs to match what was saved from manage filters
                this.selectedPOIs = [...updatedPOIs];
                
                // Update store with new selection
                this.$store.commit('setSelectedPOIs', updatedPOIs);
                
            },

            async initSliders() {
                this.$nextTick(() => {
                    const sliders = document.getElementsByClassName('cresiteiq-filter-slider');
                    HSRangeSlider.autoInit();

                    Array.from(sliders).forEach((slider, sliderIndex) => {
                        const metricSlug = slider.dataset.metric;
                        const metric = _.find(this.$store.getters.metrics, ['slug', metricSlug]);
                        if (!metric) return;

                        // Create a unique ID for this slider's event handlers
                        const sliderId = `slider-${sliderIndex}-${Date.now()}`;
                        slider.dataset.sliderId = sliderId;

                        // Initialize inputs for each handle
                        const initializeInputs = () => {
                            const handles = slider.noUiSlider.target.querySelectorAll('.noUi-handle');
                            const currentValues = slider.noUiSlider.get();
                            const valuesArray = Array.isArray(currentValues) ? currentValues : [currentValues];

                            handles.forEach((handle, handleIndex) => {
                                // Remove any existing input to prevent duplicates
                                const existingInput = handle.querySelector('input');
                                if (existingInput) {
                                    handle.removeChild(existingInput);
                                }

                                // Create new input
                                const input = document.createElement('input');
                                input.type = 'text';
                                input.className = 'absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-20 text-center form-input text-xs py-1 px-2 rounded-md border-gray-300 shadow-lg';
                                input.dataset.sliderId = sliderId;
                                input.dataset.handleIndex = handleIndex;
                                
                                // Set initial value
                                const initialValue = valuesArray[handleIndex];
                                input.value = this.formatValueForInput(initialValue, metric);
                                
                                // Add change handler
                                input.addEventListener('change', (e) => {
                                    const parsedValue = this.parseValueFromInput(e.target.value);
                                    const currentValues = slider.noUiSlider.get();
                                    let newValues = Array.isArray(currentValues) ? [...currentValues] : [currentValues];
                                    newValues = newValues.map(v => parseFloat(v));
                                    newValues[handleIndex] = parsedValue;
                                    slider.noUiSlider.set(newValues);
                                });

                                handle.appendChild(input);
                            });
                        };

                        // Initialize inputs now
                        initializeInputs();

                        // Update inputs when slider changes
                        const updateInputs = (values) => {
                            const currentValues = Array.isArray(values) ? values : [values];
                            const handles = slider.noUiSlider.target.querySelectorAll('.noUi-handle');
                            
                            handles.forEach((handle, index) => {
                                const input = handle.querySelector('input');
                                if (input) {
                                    const value = parseFloat(currentValues[index]);
                                    if (!isNaN(value)) {
                                        input.value = this.formatValueForInput(value, metric);
                                    }
                                }
                            });
                        };

                        // Set up event listeners
                        slider.noUiSlider.off('slide');
                        slider.noUiSlider.off('end');
                        slider.noUiSlider.off('set');
                        
                        slider.noUiSlider.on('slide', updateInputs);
                        slider.noUiSlider.on('end', (values) => {
                            updateInputs(values);
                            this.handleSliderEnd(slider, values);
                        });
                        slider.noUiSlider.on('set', (values) => {
                            updateInputs(values);
                            this.handleSliderEnd(slider, values);
                        });
                    });
                });
            },

            async handleSliderEnd(slider, values) {
                // Get the metric slug from the slider's data attribute
                const metricSlug = slider.getAttribute('data-metric-slug') || slider.dataset.metric;
                const metric = this.$store.getters.metrics.find(m => m.slug === metricSlug);
                
                if (!metric) return;
                
                const parsedValues = values.map(v => parseFloat(v));

                // Re-format the input that was typed into, in case it wasn't abbreviated
                const handles = slider.noUiSlider.target.querySelectorAll('.noUi-handle');
                handles.forEach((handle, index) => {
                    const input = handle.querySelector('input');
                    if (input) {
                        input.value = this.formatValueForInput(parsedValues[index], metric);
                    }
                });

                // Update the metric values directly
                this.$nextTick(() => {
                    metric.filter_level_min = Math.round(parsedValues[0]);
                    if (metric.filter_type === 'range' && parsedValues.length > 1) {
                        metric.filter_level_max = Math.round(parsedValues[1]);
                    }
                });

                if (this.creSiteIQ.boundary == 'zip' && !this.creSiteIQ.selectedStates.length) return;

                await this.$store.dispatch('filterCRESiteIQData');
                await this.$store.dispatch('refreshScores');
            },

            generateSliderJson(metric) {
                switch (metric.filter_type) {
                    case 'lt':
                        return this.generateLessThanSliderJson(metric);
                    case 'gt':
                        return this.generateGreaterThanSliderJson(metric);
                    case 'range':
                        return this.generateRangeSlider(metric);
                    default:
                        return this.generateRangeSlider(metric);
                }
            },

            generateLessThanSliderJson(metric) {
                let start = parseInt(metric.min_value);
                let avg = parseInt(metric.avg_value);
                let end = parseInt(metric.max_value);

                switch(metric.type) {
                    case 'int':
                        var formatter = {
                            "type": "integer",
                        }
                        break;
                    case 'currency':
                        formatter = {
                            "prefix": '$',
                            "type": "thousandsSeparatorAndDecimalPoints",
                        }
                        break;
                    case 'percentage':
                        formatter = {
                            "type": "integer",
                            "postfix": '%'
                        }
                        break;
                    default:
                        formatter = {
                            "type": "integer",
                        }
                }

                return JSON.stringify({
                    "start": [avg],
                    "connect": [true, false],
                    "range": {
                        "min": start,
                        "max": end
                    },
                    "tooltips": false,
                    "pips": {
                        "mode": "values",
                        "values": [start, avg, end],
                        "density": 20,
                    },
                    "formatter": formatter,
                    "cssClasses": {
                        "target": "relative h-3 rounded-full bg-gray-100 dark:bg-neutral-700 min-w-[8rem]",
                        "base": "w-full h-full relative z-1",
                        "origin": "absolute top-0 end-0 w-full h-full origin-[0_0] rounded-full",
                        "handle": "absolute top-1/2 end-0 w-5 h-5 bg-white border-4 border-blue-600 rounded-full cursor-pointer translate-x-2/4 -translate-y-2/4 dark:border-blue-500",
                        "connects": "relative z-0 w-full h-full rounded-full overflow-hidden",
                        "connect": "absolute top-0 end-0 z-1 w-full h-full bg-green-600 origin-[0_0] dark:bg-green-500",
                        "touchArea": "absolute -top-1 -bottom-1 -start-1 -end-1",
                        "tooltip": "bg-white border border-gray-200 text-xs text-gray-800 py-1 px-2 rounded-lg mb-3 absolute bottom-full start-2/4 -translate-x-2/4 whitespace-nowrap",
                        "pips": "relative w-full h-10 mt-1",
                        "value": "absolute top-4 -translate-x-2/4 text-sm text-gray-400 dark:text-neutral-500",
                        "marker": "absolute h-2 border-s border-gray-400 dark:border-neutral-500"
                    }
                });
            },

            generateGreaterThanSliderJson(metric) {
                let start = parseInt(metric.min_value);
                let avg = parseInt(metric.avg_value);
                let end = parseInt(metric.max_value);

                switch(metric.type) {
                    case 'int':
                        var formatter = {
                            "type": "integer",
                        }
                        break;
                    case 'currency':
                        formatter = {
                            "prefix": '$',
                            "type": "thousandsSeparatorAndDecimalPoints",
                        }
                        break;
                    case 'percentage':
                        formatter = {
                            "type": "integer",
                            "postfix": '%'
                        }
                        break;
                    default:
                        formatter = {
                            "type": "integer",
                        }
                }

                return JSON.stringify({
                    "start": [avg],
                    "connect": [false, true],
                    "range": {
                        "min": start,
                        "max": end
                    },
                    "tooltips": false,
                    "pips": {
                        "mode": "values",
                        "values": [start, avg, end],
                        "density": 20,
                    },
                    "formatter": formatter,
                    "cssClasses": {
                        "target": "relative h-3 rounded-full bg-gray-100 dark:bg-neutral-700 min-w-[8rem]",
                        "base": "w-full h-full relative z-1",
                        "origin": "absolute top-0 end-0 w-full h-full origin-[0_0] rounded-full",
                        "handle": "absolute top-1/2 end-0 w-5 h-5 bg-white border-4 border-blue-600 rounded-full cursor-pointer translate-x-2/4 -translate-y-2/4 dark:border-blue-500",
                        "connects": "relative z-0 w-full h-full rounded-full overflow-hidden",
                        "connect": "absolute top-0 end-0 z-1 w-full h-full bg-green-600 origin-[0_0] dark:bg-green-500",
                        "touchArea": "absolute -top-1 -bottom-1 -start-1 -end-1",
                        "tooltip": "bg-white border border-gray-200 text-xs text-gray-800 py-1 px-2 rounded-lg mb-3 absolute bottom-full start-2/4 -translate-x-2/4 whitespace-nowrap",
                        "pips": "relative w-full h-10 mt-1",
                        "value": "absolute top-4 -translate-x-2/4 text-sm text-gray-400 dark:text-neutral-500",
                        "marker": "absolute h-2 border-s border-gray-400 dark:border-neutral-500"
                    }
                });
            },

            generateRangeSlider(metric) {
                let start = parseInt(metric.min_value);
                let avg = parseInt(metric.avg_value);
                let end = parseInt(metric.max_value);

                switch(metric.type) {
                    case 'int':
                        var formatter = {
                            "type": "integer",
                        }
                        break;
                    case 'currency':
                        formatter = {
                            "prefix": '$',
                            "type": "thousandsSeparatorAndDecimalPoints",
                        }
                        break;
                    case 'percentage':
                        formatter = {
                            "type": "integer",
                            "postfix": '%'
                        }
                        break;
                    default:
                        formatter = {
                            "type": "integer",
                        }
                }

                return JSON.stringify({
                    "start": [metric.min_value, metric.max_value],
                    "range": {
                        "min": start,
                        "max": end
                    },
                    "connect": true,
                    "tooltips": false,
                    "pips": {
                        "mode": "values",
                        "values": [start, avg, end],
                        "density": 20,
                    },
                    "formatter": formatter,
                    "cssClasses": {
                        "target": "relative h-3 rounded-full bg-gray-100 dark:bg-neutral-700 min-w-[8rem]",
                        "base": "w-full h-full relative z-1",
                        "origin": "absolute top-0 end-0 w-full h-full origin-[0_0] rounded-full",
                        "handle": "absolute top-1/2 end-0 w-5 h-5 bg-white border-4 border-blue-600 rounded-full cursor-pointer translate-x-2/4 -translate-y-2/4 dark:border-blue-500",
                        "connects": "relative z-0 w-full h-full rounded-full overflow-hidden",
                        "connect": "absolute top-0 end-0 z-1 w-full h-full bg-green-600 origin-[0_0] dark:bg-green-500",
                        "touchArea": "absolute -top-1 -bottom-1 -start-1 -end-1",
                        "tooltip": "bg-white border border-gray-200 text-xs text-gray-800 py-1 px-2 rounded-lg mb-3 absolute bottom-full start-2/4 -translate-x-2/4 whitespace-nowrap",
                        "pips": "relative w-full h-10 mt-1",
                        "value": "absolute top-4 -translate-x-2/4 text-sm text-gray-400 dark:text-neutral-500",
                        "marker": "absolute h-2 border-s border-gray-400 dark:border-neutral-500"
                    }
                });
            },

            async changeFilterType(metric) {
                if (metric.filter_type == 'lt') {
                    metric.higher_is_better = false;
                } else if (metric.filter_type == 'gt') {
                    metric.higher_is_better = true;
                }

                await this.initSliders();
                await this.$store.dispatch('filterCRESiteIQData');
                await this.$store.dispatch('refreshScores');
            },
            
            setupMapBoundsListener() {
                const checkMap = () => {
                    if (this.reatlas?.map) {
                    const map = this.reatlas.map;

                    // Remove existing listeners
                    this.removeMapBoundsListener();

                    let zoomLevel = map.getZoom();

                    this.zoomChangeListener = google.maps.event.addListener(map, 'zoom_changed', () => {
                        if (this.debounceTimer) clearTimeout(this.debounceTimer);
                        this.debounceTimer = setTimeout(() => {
                        if (zoomLevel !== map.getZoom()) {
                            zoomLevel = map.getZoom();
                            if (this.canLoadPOI(zoomLevel)) {
                                this.reloadPOIsForCurrentView();
                            }
                        }
                        }, 150);
                    });

                    this.dragEndListener = google.maps.event.addListener(map, 'dragend', () => {
                        if (this.debounceTimer) clearTimeout(this.debounceTimer);
                        this.debounceTimer = setTimeout(() => {
                            if (this.canLoadPOI(zoomLevel)) {
                                this.reloadPOIsForCurrentView();
                            }
                        }, 150);
                    });

                    console.log('Map event listeners with debounce added');
                    } else {
                    setTimeout(checkMap, 500);
                    }
                };

                checkMap();
            },
            
            removeMapBoundsListener() {
                // Clear any pending timers
                if (this.debounceTimer) {
                    clearTimeout(this.debounceTimer);
                    this.debounceTimer = null;
                }
                
                if (this.zoomTimer) {
                    clearTimeout(this.zoomTimer);
                    this.zoomTimer = null;
                }
                
                // Remove zoom change listener if it exists
                if (this.zoomChangeListener) {
                    google.maps.event.removeListener(this.zoomChangeListener);
                    this.zoomChangeListener = null;
                }
                
                // Remove drag start listener if it exists
                if (this.dragStartListener) {
                    google.maps.event.removeListener(this.dragStartListener);
                    this.dragStartListener = null;
                }
                
                // Remove drag end listener if it exists
                if (this.dragEndListener) {
                    google.maps.event.removeListener(this.dragEndListener);
                    this.dragEndListener = null;
                }
                
                // Remove bounds change listener if it exists
                if (this.boundsChangeListener) {
                    google.maps.event.removeListener(this.boundsChangeListener);
                    this.boundsChangeListener = null;
                }
                
                console.log('Map event listeners removed');
            },
            
            calculateSearchRadius(zoom) {
                const zoomRadiusMap = {
                    5: 50000,
                    6: 20000,
                    7: 10000,
                    8: 5000,
                    9: 2000,
                    10: 1000,
                    11: 500,
                    12: 200,
                    13: 100,
                    14: 50,
                    15: 30,
                    16: 20,
                    17: 10,
                    18: 5
                };

                return zoomRadiusMap[zoom] || 1000; // fallback default
            },

            async reloadPOIsForCurrentView(force = false) {
                if (this.isUpdatingPOIs || !this.reatlas?.map) return;

                this.isUpdatingPOIs = true;

                try {
                    const map = this.reatlas.map;
                    const center = map.getCenter();
                    const zoom = Math.round(map.getZoom());

                    const newCenter = {
                        lat: parseFloat(center.lat().toFixed(6)),
                        lng: parseFloat(center.lng().toFixed(6)),
                        zoom: zoom
                    };

                    const hasChangedSignificantly = !this.lastCenter ||
                        Math.abs(this.lastCenter.lat - newCenter.lat) > 0.001 ||
                        Math.abs(this.lastCenter.lng - newCenter.lng) > 0.001 ||
                        Math.abs((this.lastCenter.zoom || 0) - newCenter.zoom) >= 1;

                    if (!hasChangedSignificantly && !force) {
                        console.log('Map view unchanged, skipping POI reload');
                        this.isUpdatingPOIs = false;
                        return;
                    }

                    this.lastCenter = newCenter;

                    if (!this.selectedPOIs.length) {
                        console.log('No POIs selected — skip loading');
                        this.isUpdatingPOIs = false;
                        return;
                    }
                    console.log('zoom',zoom);

                    const radius = this.calculateSearchRadius(zoom);
                    console.log('Radius:', radius);
                    const bounds = map.getBounds();
                    const ne = bounds.getNorthEast();
                    const sw = bounds.getSouthWest();

                    await this.$store.dispatch('loadNearbyPlaces', {
                        location: newCenter,
                        types: [...this.selectedPOIs],
                        radius,
                        bounds: {
                            ne: { lat: ne.lat(), lng: ne.lng() },
                            sw: { lat: sw.lat(), lng: sw.lng() }
                        },
                        zoom,
                        skipBoundsUpdate: true
                    });

                } catch (err) {
                    console.error('Error in reloadPOIsForCurrentView:', err);
                } finally {
                    this.isUpdatingPOIs = false;
                }
            }

        },
        async mounted() {
            this.$store.dispatch('loadCRESiteIQMetrics');
            this.setupMapBoundsListener();
            
            // Load POI types if not already loaded
            if (this.availablePOITypes.length === 0) {
                await this.$store.dispatch('fetchPlaceTypes');
            }

            try {
                const response = await axios.get('/admin/get-poi-setting');
                console.log('POI Setting Response:', response.data);

                // Update local value without touching the prop
                this.localPoiEnabled = response.data.poi_enabled === '1' || response.data.poi_enabled === true;

                } catch (error) {
                console.error('Error fetching POI setting:', error);
                }

        },
        beforeDestroy() {
            this.removeMapBoundsListener();
        }
    }
</script>
<style scoped>
/* Ensure the modal appears above map filters (z-index: 1000) */
.v--modal-overlay {
  z-index: 1001 !important;
}

.v--modal {
  z-index: 1002 !important;
}
</style>
